import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { 
  Shield, Smartphone, Key, Monitor, Activity, Lock, 
  Eye, EyeOff, Loader2, CheckCircle, AlertTriangle, 
  Copy, RefreshCw, LogOut, ChevronDown, ChevronUp,
  Clock, MapPin, Trash2, Download
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

export default function SecuritySettings() {
  const [securitySettings, setSecuritySettings] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSessions, setShowSessions] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  
  // 2FA State
  const [setup2FADialog, setSetup2FADialog] = useState(false);
  const [disable2FADialog, setDisable2FADialog] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  
  // Password Change State
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  
  // Export State
  const [exportDialog, setExportDialog] = useState(false);
  const [exportPassword, setExportPassword] = useState('');

  const fetchSecurityData = useCallback(async () => {
    try {
      const [settingsRes, sessionsRes, activityRes] = await Promise.all([
        api.getSecuritySettings(),
        api.getSessions(),
        api.getActivityLog(20)
      ]);
      setSecuritySettings(settingsRes.data);
      setSessions(sessionsRes.data);
      setActivityLog(activityRes.data);
    } catch (error) {
      console.error('Failed to fetch security data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSecurityData();
  }, [fetchSecurityData]);

  const handleSetup2FA = async () => {
    setProcessing(true);
    try {
      const response = await api.setup2FA();
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setBackupCodes(response.data.backup_codes);
      setSetup2FADialog(true);
    } catch (error) {
      toast.error('Failed to setup 2FA');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    setProcessing(true);
    try {
      await api.verify2FA(verificationCode);
      toast.success('Two-factor authentication enabled');
      setSetup2FADialog(false);
      setShowBackupCodes(true);
      setVerificationCode('');
      await fetchSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setProcessing(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error('Please enter a valid code');
      return;
    }
    setProcessing(true);
    try {
      await api.disable2FA(verificationCode);
      toast.success('Two-factor authentication disabled');
      setDisable2FADialog(false);
      setVerificationCode('');
      await fetchSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    try {
      await api.revokeSession(sessionId);
      toast.success('Session revoked');
      setSessions(sessions.filter(s => s.id !== sessionId));
    } catch (error) {
      toast.error('Failed to revoke session');
    }
  };

  const handleRevokeAllSessions = async () => {
    setProcessing(true);
    try {
      const response = await api.revokeAllSessions();
      toast.success(response.data.message);
      await fetchSecurityData();
    } catch (error) {
      toast.error('Failed to revoke sessions');
    } finally {
      setProcessing(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setProcessing(true);
    try {
      await api.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      await fetchSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setProcessing(false);
    }
  };

  const handleExportPortfolio = async () => {
    setProcessing(true);
    try {
      const response = await api.exportPortfolio(exportPassword || null, false);
      
      // Create download
      const blob = new Blob([response.data], { 
        type: exportPassword ? 'application/octet-stream' : 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportPassword ? 'assagart_portfolio_encrypted.bin' : 'assagart_portfolio.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Portfolio exported successfully');
      setExportDialog(false);
      setExportPassword('');
    } catch (error) {
      toast.error('Failed to export portfolio');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case 'login': return <Monitor size={14} className="text-green-400" />;
      case 'login_failed': return <AlertTriangle size={14} className="text-red-400" />;
      case '2fa_enabled': return <Shield size={14} className="text-blue-400" />;
      case '2fa_disabled': return <Shield size={14} className="text-orange-400" />;
      case 'password_changed': return <Key size={14} className="text-purple-400" />;
      case 'session_revoked': return <LogOut size={14} className="text-yellow-400" />;
      default: return <Activity size={14} className="text-muted-foreground" />;
    }
  };

  if (loading) {
    return (
      <div className="luxury-card p-8">
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="luxury-card p-8" data-testid="security-settings">
        <div className="flex items-center gap-3 mb-6">
          <Shield size={24} className="text-gold" />
          <h2 className="font-serif text-xl text-white">Security</h2>
        </div>

        <div className="space-y-4">
          {/* Two-Factor Authentication */}
          <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Smartphone size={20} className={securitySettings?.two_factor_enabled ? 'text-green-400' : 'text-muted-foreground'} />
                <div>
                  <p className="text-white text-sm font-medium">Two-Factor Authentication</p>
                  <p className="text-xs text-muted-foreground">
                    {securitySettings?.two_factor_enabled 
                      ? 'Enabled via authenticator app' 
                      : 'Add extra security to your account'}
                  </p>
                </div>
              </div>
              {securitySettings?.two_factor_enabled ? (
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded">Active</span>
                  <Button 
                    onClick={() => setDisable2FADialog(true)}
                    variant="outline"
                    size="sm"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid="disable-2fa-btn"
                  >
                    Disable
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={handleSetup2FA}
                  disabled={processing}
                  className="btn-primary"
                  data-testid="enable-2fa-btn"
                >
                  {processing ? <Loader2 size={16} className="animate-spin" /> : 'Enable'}
                </Button>
              )}
            </div>
          </div>

          {/* Change Password */}
          <button
            onClick={() => setPasswordDialog(true)}
            className="w-full p-4 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-gold/30 transition-colors text-left"
            data-testid="change-password-btn"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Key size={20} className="text-muted-foreground" />
                <div>
                  <p className="text-white text-sm font-medium">Change Password</p>
                  <p className="text-xs text-muted-foreground">
                    {securitySettings?.last_password_change 
                      ? `Last changed ${formatTimeAgo(securitySettings.last_password_change)}`
                      : 'Update your password regularly'}
                  </p>
                </div>
              </div>
              <ChevronDown size={16} className="text-muted-foreground" />
            </div>
          </button>

          {/* Active Sessions */}
          <div className="border border-[#1A1A1A]">
            <button
              onClick={() => setShowSessions(!showSessions)}
              className="w-full p-4 bg-[#0A0A0A] hover:bg-[#111111] transition-colors text-left"
              data-testid="sessions-toggle"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Monitor size={20} className="text-muted-foreground" />
                  <div>
                    <p className="text-white text-sm font-medium">Active Sessions</p>
                    <p className="text-xs text-muted-foreground">
                      {securitySettings?.active_sessions_count || 0} devices currently logged in
                    </p>
                  </div>
                </div>
                {showSessions ? (
                  <ChevronUp size={16} className="text-muted-foreground" />
                ) : (
                  <ChevronDown size={16} className="text-muted-foreground" />
                )}
              </div>
            </button>
            
            {showSessions && (
              <div className="border-t border-[#1A1A1A]">
                {sessions.length > 0 ? (
                  <>
                    <div className="max-h-64 overflow-y-auto">
                      {sessions.map((session) => (
                        <div 
                          key={session.id}
                          className="flex items-center justify-between p-4 border-b border-[#1A1A1A] last:border-b-0"
                          data-testid={`session-${session.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <Monitor size={16} className={session.is_current ? 'text-green-400' : 'text-muted-foreground'} />
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="text-white text-sm">{session.device}</p>
                                {session.is_current && (
                                  <span className="px-1.5 py-0.5 text-[10px] bg-green-500/20 text-green-400 rounded">
                                    Current
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <MapPin size={10} />
                                <span>{session.ip_address}</span>
                                <span>•</span>
                                <Clock size={10} />
                                <span>{formatTimeAgo(session.last_active)}</span>
                              </div>
                            </div>
                          </div>
                          {!session.is_current && (
                            <Button
                              onClick={() => handleRevokeSession(session.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`revoke-session-${session.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    {sessions.length > 1 && (
                      <div className="p-3 bg-[#080808] border-t border-[#1A1A1A]">
                        <Button
                          onClick={handleRevokeAllSessions}
                          disabled={processing}
                          variant="outline"
                          size="sm"
                          className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                          data-testid="revoke-all-sessions-btn"
                        >
                          {processing ? (
                            <Loader2 size={14} className="animate-spin mr-2" />
                          ) : (
                            <LogOut size={14} className="mr-2" />
                          )}
                          Sign out all other devices
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="p-4 text-center text-muted-foreground text-sm">No active sessions</p>
                )}
              </div>
            )}
          </div>

          {/* Activity Log */}
          <div className="border border-[#1A1A1A]">
            <button
              onClick={() => setShowActivity(!showActivity)}
              className="w-full p-4 bg-[#0A0A0A] hover:bg-[#111111] transition-colors text-left"
              data-testid="activity-log-toggle"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Activity size={20} className="text-muted-foreground" />
                  <div>
                    <p className="text-white text-sm font-medium">Account Activity</p>
                    <p className="text-xs text-muted-foreground">
                      Recent security events and login history
                    </p>
                  </div>
                </div>
                {showActivity ? (
                  <ChevronUp size={16} className="text-muted-foreground" />
                ) : (
                  <ChevronDown size={16} className="text-muted-foreground" />
                )}
              </div>
            </button>
            
            {showActivity && (
              <div className="border-t border-[#1A1A1A] max-h-64 overflow-y-auto">
                {activityLog.length > 0 ? (
                  activityLog.map((activity, index) => (
                    <div 
                      key={activity.id || index}
                      className="flex items-start gap-3 p-3 border-b border-[#1A1A1A] last:border-b-0"
                    >
                      {getActivityIcon(activity.action)}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm capitalize">
                          {activity.action?.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {activity.details}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatTimeAgo(activity.timestamp)}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="p-4 text-center text-muted-foreground text-sm">No recent activity</p>
                )}
              </div>
            )}
          </div>

          {/* Export Portfolio */}
          <button
            onClick={() => setExportDialog(true)}
            className="w-full p-4 bg-[#0A0A0A] border border-[#1A1A1A] hover:border-gold/30 transition-colors text-left"
            data-testid="export-portfolio-btn"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Download size={20} className="text-muted-foreground" />
                <div>
                  <p className="text-white text-sm font-medium">Export Portfolio</p>
                  <p className="text-xs text-muted-foreground">
                    Download your portfolio data securely
                  </p>
                </div>
              </div>
              <ChevronDown size={16} className="text-muted-foreground" />
            </div>
          </button>
        </div>
      </div>

      {/* 2FA Setup Dialog */}
      <Dialog open={setup2FADialog} onOpenChange={(open) => !processing && setSetup2FADialog(open)}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-white flex items-center gap-2">
              <Shield size={24} className="text-gold" />
              Setup Two-Factor Authentication
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Add Assagart to your authenticator app
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* QR Code */}
            {qrCode && (
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
              </div>
            )}

            {/* Manual Entry - More Prominent */}
            <div className="p-4 bg-[#0A0A0A] border border-[#262626] rounded-lg">
              <p className="text-sm text-white font-medium mb-2">Setup Key (for manual entry)</p>
              <div className="flex items-center gap-2 mb-3">
                <code className="flex-1 text-gold text-base font-mono break-all bg-[#111] p-2 rounded select-all">{secret}</code>
                <Button 
                  onClick={() => copyToClipboard(secret)}
                  variant="outline" 
                  size="sm"
                  className="shrink-0 border-[#333] hover:bg-[#222]"
                >
                  <Copy size={14} />
                </Button>
              </div>
              
              {/* Instructions for different apps */}
              <div className="text-xs text-muted-foreground space-y-2 border-t border-[#262626] pt-3 mt-3">
                <p className="font-medium text-gray-400">How to add:</p>
                <div className="space-y-1.5">
                  <p><span className="text-white">Apple Passwords (iOS 18+):</span> Settings → Passwords → Add Account → Setup Verification Code → Enter Setup Key</p>
                  <p><span className="text-white">Google Authenticator:</span> Tap + → Enter setup key</p>
                  <p><span className="text-white">Microsoft Authenticator:</span> Add account → Other → Enter key manually</p>
                </div>
              </div>
            </div>

            {/* Verification Input */}
            <div>
              <label className="block text-sm text-muted-foreground mb-2">
                Enter the 6-digit code from your authenticator app
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full p-3 bg-[#0A0A0A] border border-[#1A1A1A] text-white text-center text-2xl tracking-[0.5em] font-mono focus:border-gold outline-none rounded"
                maxLength={6}
                data-testid="2fa-code-input"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Codes refresh every 30 seconds. Make sure your device time is set automatically.
              </p>
            </div>

            <Button 
              onClick={handleVerify2FA}
              disabled={processing || verificationCode.length !== 6}
              className="btn-primary w-full"
              data-testid="verify-2fa-btn"
            >
              {processing ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify and Enable'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-white flex items-center gap-2">
              <Key size={24} className="text-gold" />
              Backup Codes
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Save these codes in a secure place. Each can only be used once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A] rounded">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <code key={index} className="text-gold text-sm font-mono p-2 bg-[#111111] rounded text-center">
                    {code}
                  </code>
                ))}
              </div>
            </div>

            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
              <div className="flex gap-2">
                <AlertTriangle size={16} className="text-yellow-400 shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-400">
                  If you lose access to your authenticator app, you can use these codes to sign in.
                  Store them securely.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={() => copyToClipboard(backupCodes.join('\n'))}
                variant="outline"
                className="flex-1"
              >
                <Copy size={14} className="mr-2" />
                Copy Codes
              </Button>
              <Button 
                onClick={() => setShowBackupCodes(false)}
                className="btn-primary flex-1"
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={disable2FADialog} onOpenChange={(open) => !processing && setDisable2FADialog(open)}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-white flex items-center gap-2">
              <AlertTriangle size={24} className="text-red-500" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Enter a code from your authenticator app to disable 2FA
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
              <p className="text-xs text-red-400">
                Disabling 2FA will make your account less secure. Only do this if necessary.
              </p>
            </div>

            <div>
              <label className="block text-sm text-muted-foreground mb-2">
                Enter verification code or backup code
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="Enter code"
                className="w-full p-3 bg-[#0A0A0A] border border-[#1A1A1A] text-white text-center text-xl tracking-wider font-mono focus:border-gold outline-none"
                maxLength={8}
                data-testid="disable-2fa-code-input"
              />
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={() => {
                  setDisable2FADialog(false);
                  setVerificationCode('');
                }}
                disabled={processing}
                className="btn-secondary flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDisable2FA}
                disabled={processing || verificationCode.length < 6}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-disable-2fa-btn"
              >
                {processing ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Disabling...
                  </>
                ) : (
                  'Disable 2FA'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={(open) => !processing && setPasswordDialog(open)}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-white flex items-center gap-2">
              <Key size={24} className="text-gold" />
              Change Password
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Enter your current password and choose a new one
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Current Password</label>
              <div className="relative">
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full p-3 pr-10 bg-[#0A0A0A] border border-[#1A1A1A] text-white focus:border-gold outline-none"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white"
                >
                  {showPasswords ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-muted-foreground mb-2">New Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-3 bg-[#0A0A0A] border border-[#1A1A1A] text-white focus:border-gold outline-none"
                data-testid="new-password-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Must be at least 8 characters with uppercase, lowercase, number, and special character
              </p>
            </div>

            <div>
              <label className="block text-sm text-muted-foreground mb-2">Confirm New Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-3 bg-[#0A0A0A] border border-[#1A1A1A] text-white focus:border-gold outline-none"
                data-testid="confirm-password-input"
              />
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={() => {
                  setPasswordDialog(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                disabled={processing}
                className="btn-secondary flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleChangePassword}
                disabled={processing || !currentPassword || !newPassword || !confirmPassword}
                className="btn-primary flex-1"
                data-testid="confirm-change-password-btn"
              >
                {processing ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  'Change Password'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialog} onOpenChange={(open) => !processing && setExportDialog(open)}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-white flex items-center gap-2">
              <Download size={24} className="text-gold" />
              Export Portfolio
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Download your portfolio data as JSON
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">
                Encryption Password (optional)
              </label>
              <input
                type="password"
                value={exportPassword}
                onChange={(e) => setExportPassword(e.target.value)}
                placeholder="Leave empty for unencrypted export"
                className="w-full p-3 bg-[#0A0A0A] border border-[#1A1A1A] text-white focus:border-gold outline-none"
                data-testid="export-password-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {exportPassword 
                  ? 'Your export will be encrypted' 
                  : 'Your export will be in plain JSON format'}
              </p>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={() => {
                  setExportDialog(false);
                  setExportPassword('');
                }}
                disabled={processing}
                className="btn-secondary flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleExportPortfolio}
                disabled={processing}
                className="btn-primary flex-1"
                data-testid="confirm-export-btn"
              >
                {processing ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={16} className="mr-2" />
                    Export
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
